import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { EVENT_TYPES } from "@/lib/eventTypes";
import { Trash2, Mail, Phone, Calendar, Users, Compass, ChevronDown, ChevronUp } from "lucide-react";
import { format } from "date-fns";
import { fr as frLocale } from "date-fns/locale";

function formatEventDate(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return iso;
  try {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const s = format(d, "EEEE d MMMM yyyy", { locale: frLocale });
    return s.charAt(0).toUpperCase() + s.slice(1);
  } catch (_) { return iso; }
}

function SourceBadge({ attribution }) {
  const [open, setOpen] = useState(false);
  if (!attribution || Object.keys(attribution).length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-[#D9D3C5] text-[#697585]">
        <Compass size={12} /> Source inconnue
      </span>
    );
  }
  const label = attribution.source_label || "Source non identifiée";
  const hasDetails = attribution.referrer || attribution.landing_page || Object.keys(attribution).some((k) => k.startsWith("utm_") || ["gclid","fbclid","msclkid","ttclid"].includes(k));
  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-[#B8956A]/40 bg-[#B8956A]/5 text-[#203240] hover:bg-[#B8956A]/10"
        data-testid="source-badge"
      >
        <Compass size={12} className="text-[#B8956A]" />
        <span className="font-medium">Source :</span> {label}
        {hasDetails && (open ? <ChevronUp size={12}/> : <ChevronDown size={12}/>)}
      </button>
      {open && hasDetails && (
        <div className="mt-2 border border-[#D9D3C5] bg-[#F6F0E7]/50 p-3 text-xs text-[#203240] space-y-1" data-testid="source-details">
          {attribution.referrer && <div><span className="text-[#697585]">Referrer :</span> <span className="break-all">{attribution.referrer}</span></div>}
          {attribution.landing_page && <div><span className="text-[#697585]">Page d&apos;atterrissage :</span> {attribution.landing_page}</div>}
          {attribution.utm_source && <div><span className="text-[#697585]">utm_source :</span> {attribution.utm_source}</div>}
          {attribution.utm_medium && <div><span className="text-[#697585]">utm_medium :</span> {attribution.utm_medium}</div>}
          {attribution.utm_campaign && <div><span className="text-[#697585]">utm_campaign :</span> {attribution.utm_campaign}</div>}
          {attribution.utm_content && <div><span className="text-[#697585]">utm_content :</span> {attribution.utm_content}</div>}
          {attribution.utm_term && <div><span className="text-[#697585]">utm_term :</span> {attribution.utm_term}</div>}
          {attribution.gclid && <div><span className="text-[#697585]">gclid :</span> {attribution.gclid}</div>}
          {attribution.fbclid && <div><span className="text-[#697585]">fbclid :</span> {attribution.fbclid}</div>}
          {attribution.msclkid && <div><span className="text-[#697585]">msclkid :</span> {attribution.msclkid}</div>}
          {attribution.ttclid && <div><span className="text-[#697585]">ttclid :</span> {attribution.ttclid}</div>}
        </div>
      )}
    </div>
  );
}

function PrepareReception({ request }) {
  const nav = useNavigate();
  const [type, setType] = useState("");
  const [error, setError] = useState("");

  const prepare = async () => {
    if (!type) { setError("Choisissez le type d'événement."); return; }
    setError("");
    try {
      const { data } = await api.post(`/admin/event-requests/${request.id}/prepare`, { event_type: type });
      nav(`/admin/receptions/${data.id}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Préparation impossible");
    }
  };

  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-dashed border-[#D9D3C5] pt-4">
      <select data-testid={`prepare-type-${request.id}`} className="field w-56" value={type} onChange={(ev) => setType(ev.target.value)}>
        <option value="">Préparer une réception…</option>
        {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      <button data-testid={`prepare-btn-${request.id}`} className="btn-ghost" onClick={prepare}>Créer le dossier</button>
      <span className="text-xs text-[#697585]">La proposition n'est jamais marquée comme acceptée automatiquement.</span>
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}

export function EventsAdmin() {  const [items, setItems] = useState([]);
  const load = () => api.get("/events/requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => { if (window.confirm("Supprimer ?")) { await api.delete(`/events/requests/${id}`); load(); } };

  return (
    <div data-testid="admin-events-page">
      <p className="overline">Demandes</p>
      <h1 className="font-display text-4xl md:text-5xl mt-3">Événements privés</h1>
      <p className="text-[#697585] mt-2">Demandes de devis reçues via le site.</p>

      <div className="mt-10 space-y-4">
        {items.length === 0 && <p className="text-[#697585]">Aucune demande pour l&apos;instant.</p>}
        {items.map((e) => (
          <div key={e.id} className="border border-[#D9D3C5] bg-[#FFFFFF] p-6">
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <div className="flex items-baseline gap-3 flex-wrap">
                  <p className="font-display text-2xl">{e.name}</p>
                  <span className="overline">{e.event_type}</span>
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-[#697585]">
                  <div className="flex items-center gap-2"><Mail size={14}/> {e.email}</div>
                  {e.phone && <div className="flex items-center gap-2"><Phone size={14}/> {e.phone}</div>}
                  {e.event_date && <div className="flex items-center gap-2"><Calendar size={14}/> {formatEventDate(e.event_date)}</div>}
                  {!!e.guests && <div className="flex items-center gap-2"><Users size={14}/> {e.guests} convives</div>}
                  {e.origin && <div>Nous a connu par : <span className="text-[#203240]">{e.origin}</span></div>}
                  {e.page_source && <div>Page du formulaire : <span className="text-[#203240]">{e.page_source}</span></div>}
                </div>
                {e.message && <p className="mt-4 text-[#203240]">{e.message}</p>}
                <SourceBadge attribution={e.attribution} />
                <p className="mt-3 text-xs text-[#697585]">Reçu le {new Date(e.created_at).toLocaleString("fr-FR")}</p>
                <PrepareReception request={e} />
              </div>
              <button onClick={() => del(e.id)} className="p-2 border border-[#D9D3C5] hover:border-red-400 hover:text-red-400"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ContactAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/contact/messages").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (window.confirm("Supprimer ?")) { await api.delete(`/contact/messages/${id}`); load(); } };

  return (
    <div data-testid="admin-messages-page">
      <p className="overline">Boîte de réception</p>
      <h1 className="font-display text-4xl md:text-5xl mt-3">Messages contact</h1>

      <div className="mt-10 space-y-4">
        {items.length === 0 && <p className="text-[#697585]">Aucun message.</p>}
        {items.map((m) => (
          <div key={m.id} className="border border-[#D9D3C5] bg-[#FFFFFF] p-6">
            <div className="flex justify-between items-start gap-4">
              <div className="flex-1">
                <p className="font-display text-xl">{m.name} <span className="text-[#B8956A] text-base ml-2">{m.subject}</span></p>
                <div className="mt-2 text-sm text-[#697585] flex flex-wrap gap-4">
                  <span className="flex items-center gap-2"><Mail size={14}/>{m.email}</span>
                  {m.phone && <span className="flex items-center gap-2"><Phone size={14}/>{m.phone}</span>}
                </div>
                <p className="mt-4 text-[#203240] whitespace-pre-wrap">{m.message}</p>
                <p className="mt-3 text-xs text-[#697585]">Reçu le {new Date(m.created_at).toLocaleString("fr-FR")}</p>
              </div>
              <button onClick={() => del(m.id)} className="p-2 border border-[#D9D3C5] hover:border-red-400 hover:text-red-400"><Trash2 size={14}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
